import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import lottie from 'lottie-web/build/player/lottie';
import { themeSettings } from '../../js/settings';
import cybersmartNetworkAnimationData from './lottie/animations/hero-animation.json';

const LOTTIE_ANIMATIONS_BY_SLUG = {
	'cybersmart-network': cybersmartNetworkAnimationData,
};

/**
 * Returns animation data copy with resolved image URLs from webpack assets.
 *
 * @param {string} animationSlug Selected animation slug.
 * @param {string} templateUrl Theme template URL.
 *
 * @return {?Object} Lottie animation data.
 */
const getLottieAnimationData = ( animationSlug, templateUrl ) => {

	const sourceData = LOTTIE_ANIMATIONS_BY_SLUG[ animationSlug ];
	if ( ! sourceData ) {

		return null;

	}

	const animationData = JSON.parse( JSON.stringify( sourceData ) );
	animationData.assets = ( animationData.assets || [] ).map( ( asset ) => {

		if ( ! asset.p || ! asset.p.endsWith( '.png' ) ) {

			return asset;

		}

		return {
			...asset,
			u: '',
			p: `${ templateUrl }/img/blocks/hero-animated-lottie/${ asset.p }`,
			e: 0,
		};

	} );

	return animationData;

};

/**
 * Mounts Lottie animation inside target container.
 *
 * @param {?HTMLElement} container Target element for animation.
 *
 * @return {?Object} Lottie instance.
 */
const mountLottieAnimation = ( container ) => {

	if ( ! container ) {

		return null;

	}

	const animationSlug = container.dataset.lottieAnimation || 'cybersmart-network';
	const templateUrl = themeSettings?.templateUrl || '';
	const animationData = getLottieAnimationData( animationSlug, templateUrl );
	if ( ! animationData ) {

		return null;

	}

	return lottie.loadAnimation( {
		container,
		renderer: 'svg',
		loop: true,
		autoplay: true,
		animationData,
	} );

};

/**
 * Plays intro fade/blur animation for content and media.
 *
 * @param {?HTMLElement} content Content element.
 * @param {?HTMLElement} media Media element.
 *
 * @return {Object} GSAP timeline instance.
 */
const animateIntro = ( content, media ) => {

	const introTl = gsap.timeline( );

	if ( content ) {

		introTl.fromTo( content, {
			opacity: 0,
			filter: 'blur(8px)',
			y: 16,
		}, {
			opacity: 1,
			filter: 'blur(0px)',
			y: 0,
			duration: 1.05,
			ease: 'power3.out',
		} );

	}

	if ( media ) {

		introTl.fromTo( media, {
			opacity: 0,
			filter: 'blur(8px)',
			y: 16,
		}, {
			opacity: 1,
			filter: 'blur(0px)',
			y: 0,
			duration: 1.05,
			ease: 'power3.out',
		}, '-=0.85' );

	}

	return introTl;

};

/**
 * Creates scroll-driven fade out animation for sticky content.
 *
 * @param {HTMLElement} content Content element.
 * @param {?HTMLElement} media Media element.
 *
 * @return {Object} GSAP tween instance.
 */
const createContentFadeOut = ( content, media ) => {

	/**
	 * Returns numeric CSS value for a property.
	 *
	 * @param {HTMLElement} element Source element.
	 * @param {string} propertyName CSS property name.
	 *
	 * @return {number} Parsed numeric value or 0.
	 */
	const getNumericCssValue = ( element, propertyName ) => {

		const parsedValue = Number.parseFloat( window.getComputedStyle( element )[ propertyName ] );
		return Number.isFinite( parsedValue ) ? parsedValue : 0;

	};

	/**
	 * Calculates scroll distance used by content fade trigger.
	 *
	 * @return {number} Fade distance in pixels.
	 */
	const getFadeDistance = () => {

		if ( ! media ) {

			return 320;

		}

		const mediaTop = media.offsetTop + getNumericCssValue( media, 'top' );
		const contentTop = content.offsetTop + getNumericCssValue( content, 'top' );
		return Math.max( mediaTop - contentTop, 1 );

	};

	return gsap.fromTo( content, {
		opacity: 1,
		filter: 'blur(0px)',
		y: 0,
	}, {
		opacity: 0,
		filter: 'blur(12px)',
		y: 16,
		ease: 'none',
		immediateRender: false,
		scrollTrigger: {
			trigger: content,
			start: 'top 120px',
			end: () => `+=${ getFadeDistance( ) }`,
			scrub: true,
			invalidateOnRefresh: true,
		},
	} );

};

/**
 * Scales image from smaller size to full size while scrolling.
 *
 * @param {HTMLElement} block Block root element.
 * @param {?HTMLElement} visual Media visual element.
 *
 * @return {?Object} GSAP tween instance or null.
 */
const createMediaScaleOnScroll = ( block, visual ) => {

	if ( ! visual ) {

		return null;

	}

	return gsap.to( visual, {
		scale: 1,
		transformOrigin: '50% 0%',
		ease: 'none',
		scrollTrigger: {
			trigger: block.querySelector( '.b-hero-animated__content' ) || block,
			start: 'top 120px',
			endTrigger: block,
			end: 'bottom bottom',
			scrub: true,
			invalidateOnRefresh: true,
		},
	} );

};

let hasBoundResizeRefresh = false;

/**
 * Binds one debounced resize listener that refreshes ScrollTrigger.
 *
 * @return {void}
 */
const bindResizeRefresh = () => {

	if ( ! ScrollTrigger || hasBoundResizeRefresh ) {

		return;

	}

	let resizeTimeoutId;

	window.addEventListener( 'resize', () => {

		window.clearTimeout( resizeTimeoutId );
		resizeTimeoutId = window.setTimeout( () => {

			ScrollTrigger.refresh( );

		}, 150 );

	}, { passive: true } );

	hasBoundResizeRefresh = true;

};

/**
 * Checks if element intersects the vertical center line of viewport.
 *
 * @param {HTMLElement} element Element to check.
 *
 * @return {boolean} True when element covers viewport middle.
 */
const isInViewportAtHalfScreen = ( element ) => {

	const rect = element.getBoundingClientRect( );
	const viewportMiddle = window.innerHeight * 0.5;
	return rect.top <= viewportMiddle && rect.bottom >= viewportMiddle;

};

/**
 * Resolves when window load event has fired.
 *
 * @return {Promise<void>} Promise resolved after full page load.
 */
const waitForWindowLoad = () => {

	if ( document.readyState === 'complete' ) {

		return Promise.resolve( );

	}

	return new Promise( ( resolve ) => {

		window.addEventListener( 'load', () => resolve( ), { once: true } );

	} );

};

/**
 * Resolves when image is ready for rendering.
 *
 * @param {?HTMLImageElement} image Image to wait for.
 *
 * @return {Promise<void>} Promise resolved when image can be painted.
 */
const waitForImageReady = ( image ) => {

	if ( ! image ) {

		return Promise.resolve( );

	}

	if ( image.complete ) {

		return Promise.resolve( );

	}

	if ( typeof image.decode === 'function' ) {

		return image.decode( ).catch( () => Promise.resolve( ) );

	}

	return new Promise( ( resolve ) => {

		image.addEventListener( 'load', () => resolve( ), { once: true } );
		image.addEventListener( 'error', () => resolve( ), { once: true } );

	} );

};

const DESKTOP_BREAKPOINT_QUERY = '(min-width: 1025px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const HERO_ANIMATED_CLASS = 'b-hero-animated--animated';

document.querySelectorAll( '.b-hero-animated' ).forEach( ( block ) => {

	const content = block.querySelector( '.b-hero-animated__content' );
	const media = block.querySelector( '.b-hero-animated__media' );
	const lottieContainer = block.querySelector( '.b-hero-animated__lottie' );
	const visual = block.querySelector( '.b-hero-animated__visual' );
	mountLottieAnimation( lottieContainer );
	const targets = [ content, media, visual ].filter( Boolean );

	if ( ! targets.length ) {

		return;

	}

	const desktopViewportQuery = window.matchMedia( DESKTOP_BREAKPOINT_QUERY );
	const reducedMotionQuery = window.matchMedia( REDUCED_MOTION_QUERY );
	let teardownDesktopAnimations = null;
	let currentMode = '';
	let resizeTimeoutId;

	/**
	 * Clears inline motion styles so CSS can control the visual state.
	 *
	 * @return {void}
	 */
	const clearInlineMotionStyles = () => {

		targets.forEach( ( target ) => {

			target.style.removeProperty( 'opacity' );
			target.style.removeProperty( 'filter' );
			target.style.removeProperty( 'transform' );

		} );

	};

	/**
	 * Applies static (non-animated) visual state.
	 *
	 * @return {void}
	 */
	const applyStaticState = () => {

		block.classList.remove( HERO_ANIMATED_CLASS );

		targets.forEach( ( target ) => {

			target.style.opacity = '1';
			target.style.filter = 'none';
			target.style.transform = 'none';

		} );

	};

	/**
	 * Initializes desktop-only animation and returns teardown callback.
	 *
	 * @return {Function} Cleanup function.
	 */
	const setupDesktopAnimations = () => {

		if ( ScrollTrigger ) {

			gsap.registerPlugin( ScrollTrigger );
			bindResizeRefresh( );

		}

		block.classList.add( HERO_ANIMATED_CLASS );
		clearInlineMotionStyles( );

		let introTimeline = null;
		let contentFadeTween = null;
		let imageScaleTween = null;
		let introTrigger = null;
		let introPlayed = false;
		let isActive = true;
		const startsInViewport = isInViewportAtHalfScreen( block );
		let canPlayIntro = ! startsInViewport || document.readyState === 'complete';

		/**
		 * Plays intro animation once and then sets content fade out trigger.
		 *
		 * @return {void}
		 */
		const playIntro = () => {

			if ( introPlayed || ! isActive ) {

				return;

			}

			introPlayed = true;
			introTimeline = animateIntro( content, media );

			if ( content && ScrollTrigger ) {

				introTimeline.eventCallback( 'onComplete', () => {

					if ( ! isActive ) {

						return;

					}

					contentFadeTween = createContentFadeOut( content, media );

				} );

			}

		};

		/**
		 * Plays intro only after initial-page gating is released.
		 *
		 * @return {void}
		 */
		const requestPlayIntro = () => {

			if ( ! canPlayIntro || ! isActive ) {

				return;

			}

			playIntro( );

		};

		if ( ScrollTrigger ) {

			introTrigger = ScrollTrigger.create( {
				trigger: block,
				start: 'top 50%',
				onEnter: requestPlayIntro,
				onEnterBack: requestPlayIntro,
			} );

			if ( startsInViewport ) {

				const imageElement = visual instanceof HTMLImageElement ? visual : null;

				Promise.all( [ waitForWindowLoad( ), waitForImageReady( imageElement ) ] ).then( () => {

					if ( ! isActive ) {

						return;

					}

					canPlayIntro = true;
					ScrollTrigger.refresh( );
					window.requestAnimationFrame( () => {

						requestPlayIntro( );

					} );

				} );

			} else {

				requestPlayIntro( );

			}

			imageScaleTween = createMediaScaleOnScroll( block, visual );

		} else {

			playIntro( );

		}

		return () => {

			isActive = false;

			introTrigger?.kill( );
			introTimeline?.kill( );
			contentFadeTween?.kill( );
			imageScaleTween?.kill( );

			block.classList.remove( HERO_ANIMATED_CLASS );
			clearInlineMotionStyles( );

		};

	};

	/**
	 * Switches between animated and static modes based on viewport and motion settings.
	 *
	 * @return {void}
	 */
	const updateMode = () => {

		const shouldAnimate = desktopViewportQuery.matches && ! reducedMotionQuery.matches;

		if ( shouldAnimate ) {

			if ( currentMode === 'animated' ) {

				return;

			}

			teardownDesktopAnimations?.( );
			teardownDesktopAnimations = setupDesktopAnimations( );
			currentMode = 'animated';
			return;

		}

		if ( currentMode === 'static' ) {

			return;

		}

		teardownDesktopAnimations?.( );
		teardownDesktopAnimations = null;
		applyStaticState( );
		currentMode = 'static';

		if ( ScrollTrigger ) {

			ScrollTrigger.refresh( );

		}

	};

	/**
	 * Debounced resize handler to keep mode state consistent while dragging window size.
	 *
	 * @return {void}
	 */
	const handleResize = () => {

		window.clearTimeout( resizeTimeoutId );
		resizeTimeoutId = window.setTimeout( () => {

			updateMode( );

		}, 150 );

	};

	desktopViewportQuery.addEventListener( 'change', updateMode );
	reducedMotionQuery.addEventListener( 'change', updateMode );
	window.addEventListener( 'resize', handleResize, { passive: true } );

	updateMode( );

} );
